import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";

import TopBar from "components/dashboardParts/TopBar";
import APIRequest from "system/APIRequest";
import QueryMonitorDialog from "../../components/queryMonitors/QueryMonitorDialog";
import { QueryMonitor, QueryMonitorJSON } from "system/QueryMonitor";
import QueryMonitorList from "components/queryMonitors/QueryMonitorList";
import { currentUser, permissionTest } from "system/InfegyUser";
import { useNavigate } from "react-router-dom";
import { TextLoader } from "components/apiHandling/TextLoader";
import { setDocumentTitle } from "system/Utilities";

export default function QueryMonitorsPage(): React.JSX.Element {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const user = currentUser();

  const [queryMonitors, setQueryMonitors] = useState<Array<QueryMonitor>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showQueryMonitor, setShowQueryMonitor] = useState<boolean>(false);

  const queryMonitorLimit = permissionTest("extra_query_alerts") ? 20 : 5;
  const canCreateQueryMonitor = queryMonitors.length < queryMonitorLimit;

  async function getQueryMonitors() {
    setIsLoading(true);
    const response = await APIRequest("obj/query-monitor");
    const result = response.output.map((queryMonitorJSON: QueryMonitorJSON) => {
      return new QueryMonitor(queryMonitorJSON);
    });
    setQueryMonitors(result);
    setIsLoading(false);
  }

  useEffect(() => {
    const hasQueryMonitorsAccess = user?.permissions.includes("query_alerts")
      && user.permissions.includes("social_data");
    if (!hasQueryMonitorsAccess) {
      navigate("/start", { replace: true });
      return;
    }
    getQueryMonitors();
  }, []);

  useEffect(() => setDocumentTitle(i18n.t("monitors")), []);

  return (
    <Box sx={{ maxWidth: "1300px", margin: "0 auto", position: "relative", pl: 2, pr: 2 }}>
      <TopBar showLogo={true} title={t("query-monitors-page.title")} />
      <Box sx={{
        textAlign: "center"
      }}>
        <Button color="success" disabled={!canCreateQueryMonitor} onClick={() => setShowQueryMonitor(true)} variant="contained">
          {t("query-monitors-page.create-button")}
        </Button>
        {!canCreateQueryMonitor &&
          <Alert severity="warning" sx={{marginTop: 1}} >
            {t("query-monitors-page.monitor-limit-reached")}
          </Alert>
        }
      </Box>
      <Paper sx={{marginTop: 1}}>
        {isLoading &&
          <Box
            sx={{
              textAlign: "center",
              padding: 1
            }}>
            <TextLoader />
          </Box>
          || queryMonitors.length > 0 &&
          <QueryMonitorList 
            onChange={() => getQueryMonitors()}
            queryMonitors={queryMonitors} />
          ||
          <Alert severity="info" sx={{marginBottom: 1}}>
            {t("query-monitors-page.getting-started")}
          </Alert>
        }
      </Paper>
      {showQueryMonitor &&
        <QueryMonitorDialog 
          onChange={() => getQueryMonitors()}
          onClose={() => setShowQueryMonitor(false)} 
          open={showQueryMonitor}
        />
      }
    </Box>
  );
}